import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { Box, Button, Stack, Typography } from "@mui/material";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";

import { BaseModal } from "component/Modal/BaseModal";
import { ScheduleItem } from "types/response/schedule";
import LoadingButton from "component/Button/LoadingButton";
import { dateToHourMinute, safeHourMinuteToDate } from "utils/time";
import {
  EXTERNAL_SERVICE_CODE,
  ExternalServiceCodeType,
} from "types/externalService";

import { ScheduleForm } from "./ScheduleForm";
import { MapOption, ScheduleFormInput, scheduleSchema } from "./type";
import { UpdateScheduleRequest } from "types/schedule";
import { alreadyExists } from "./helper";
import { recordToCleaningSetting } from "utils/robot/cleaningSetting/cleaningSetting";
import { useEffect } from "react";

type EditModalProps = {
  exSrvCode: ExternalServiceCodeType;
  schedule: ScheduleItem;
  robotId: string;
  maps: MapOption[];
  schedules: ScheduleItem[];
  open: boolean;
  onClose: () => void;
  onUpdate: (input: UpdateScheduleRequest) => void;
  isUpdating: boolean;
};

export const ScheduleEditModal = ({
  exSrvCode,
  schedule,
  robotId,
  open,
  onClose,
  maps,
  schedules,
  onUpdate,
  isUpdating,
}: EditModalProps) => {
  const {
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ScheduleFormInput>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      scheduleName: schedule.scheduleName,
      dayOfWeek: schedule.dayOfWeek.toString(),
      time: safeHourMinuteToDate(schedule.hour, schedule.minute),
      mapId: schedule.mapId,
      memo: schedule.memo,
      cleaningSettings: schedule.cleaningSettings.reduce((acc, cur) => {
        acc[cur.type] = cur.value;
        return acc;
      }, {} as Record<string, string>),
    },
  });

  const selectedCleanType = useWatch({
    control,
    name: "cleaningSettings.cleanType",
  });
  const selectedRoute = useWatch({
    control,
    name: "cleaningSettings.route",
  });

  useEffect(() => {
    if (exSrvCode !== EXTERNAL_SERVICE_CODE.DREAME) return;

    if (
      ["1", "5"].includes(selectedCleanType) &&
      ["2", "3"].includes(selectedRoute)
    ) {
      setValue("cleaningSettings.route", "1");
    }
  }, [exSrvCode, setValue, selectedCleanType, selectedRoute]);

  const onSubmit = async (data: ScheduleFormInput) => {
    const { hour, minute } = dateToHourMinute(data.time);
    const dayNum = Number(data.dayOfWeek);
    if (isNaN(dayNum) || dayNum < 0 || 6 < dayNum) {
      setError("dayOfWeek", { type: "manual", message: "不正な値です" });
      return;
    }

    if (alreadyExists({ dayOfWeek: dayNum, hour, minute }, schedules)) {
      setError("time", {
        type: "manual",
        message: "すでにスケジュールが設定されています",
      });
      return;
    }
    onUpdate({
      ...data,
      hour,
      minute,
      dayOfWeek: dayNum,
      cleaningSettings: recordToCleaningSetting(data.cleaningSettings),
      robotId,
      scheduleId: schedule.scheduleId,
    });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlineIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography>スケジュール編集</Typography>
        </Box>
      }
      children={
        <form onSubmit={handleSubmit(onSubmit)} id="schedule-form">
          <ScheduleForm
            exSrvCode={exSrvCode}
            errors={errors}
            control={control}
            maps={maps}
          />
        </form>
      }
      actions={
        <Stack direction={"row"} spacing={2}>
          <Button
            disabled={isUpdating}
            variant="outlined"
            onClick={onClose}
            autoFocus
          >
            キャンセル
          </Button>
          <LoadingButton
            isLoading={isUpdating}
            disabled={!isDirty}
            variant="contained"
            type="submit"
            form="schedule-form"
            sx={{ minWidth: "120px" }}
          >
            保存
          </LoadingButton>
        </Stack>
      }
    />
  );
};
